import * as React from "react";
import { Paper, Stack, Tooltip } from "@mui/material";
import InsightsIcon from "@mui/icons-material/Insights";
import { useAuth } from "src/components/providers/auth-context";
import styled from "styled-components";
import AdvancedInsightsRadioGroupRating from "src/scenes/dashboard/overview-components/advanced-insights-assets/advanced-insights-user-opinion";
import AiInsightsModal from "src/components/ai-insights/ai-insights-modal";
import request from "src/utils/request";

type AiInsight = {
    id: string;
    title: string;
    type: "anomaly" | "behavioural" | "baseline" | "causal";
    body: string;
    thumbs_up: number;
    thumbs_down: number;
    reaction: "up" | "down" | "none";
    teams?: string[];
};

const limitTextToCharacters = (text, charLimit = 300) => {
    const limitedText = text.length > charLimit ? `${text.slice(0, charLimit)}...` : text;
    const remainingText = text.length > charLimit ? text.slice(charLimit) : "";
    return { limitedText, remainingText };
};

const SliderContainer = styled.div({
    display: "flex",
    overflowX: "auto",
    gap: "16px",
    padding: "8px 0",
    "&::-webkit-scrollbar": {
        height: "8px",
        backgroundColor: "#2d2d2d",
    },
    "&::-webkit-scrollbar-thumb": {
        backgroundColor: "#484646",
        borderRadius: "4px",
    },
});

const InsightsPaper = styled(Paper)`
    border-radius: 14px;
    min-width: 300px;
`;
const AiInsightsSection = () => {
    const [aiInsights, setAiInsights] = React.useState<AiInsight[]>([]);
    const [modalOpen, setModalOpen] = React.useState<string>("");
    const { isDemo } = useAuth();
    const getAiInsights = async () => {
        let response;
        if (isDemo) {
            // @ts-expect-error: Ignore TS1323 for dynamic import
            response = (await import("src/components/ai-insights/ai-insights-mock-data")).default;
        } else {
            response = await request(`/api/data/v1/insights`);
        }
        setAiInsights(response);
    };

    React.useEffect(() => {
        getAiInsights();
    }, []);

    const getTitle = (insight: AiInsight) => {
        if (insight.type === "behavioural")
            return (
                <Tooltip title="Behavioural are the insights that are not related to a recent anomaly or change but with the general way of working observed by the system.">
                    <p
                        style={{
                            fontSize: "12px",
                            textDecoration: "underline dotted",
                        }}
                    >
                        Behavioural Insight
                    </p>
                </Tooltip>
            );
        if (insight.type === "baseline")
            return (
                <Tooltip title="Baseline are the insights that are not related to a sudden anomaly but with a change on the baseline of a value observed by the system.">
                    <p
                        style={{
                            fontSize: "12px",
                            textDecoration: "underline dotted",
                        }}
                    >
                        Baseline Insight
                    </p>
                </Tooltip>
            );
        if (insight.type === "anomaly")
            return (
                <Tooltip title="Anomaly are the insights that are related to a deteced anomaly.">
                    <p
                        style={{
                            fontSize: "12px",
                            textDecoration: "underline dotted",
                        }}
                    >
                        Anomaly Insight
                    </p>
                </Tooltip>
            );
        if (insight.type === "causal")
            return (
                <Tooltip title="Causal are the insights that are generated by analysing the causal relations of variables using Causal AI.">
                    <p
                        style={{
                            fontSize: "12px",
                            textDecoration: "underline dotted",
                        }}
                    >
                        Causal Insight
                    </p>
                </Tooltip>
            );

        return <p style={{ fontSize: "12px" }}>Insight</p>;
    };

    if (!aiInsights || !aiInsights.length) return null;

    return (
        <Stack>
            <h4 style={{ marginTop: 0 }}>Insights</h4>
            <SliderContainer container spacing={2}>
                {aiInsights &&
                    aiInsights.map((insight, index) => {
                        const { limitedText, remainingText } = limitTextToCharacters(
                            insight.body,
                            300
                        );
                        return (
                            <InsightsPaper>
                                <Stack>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        spacing={0.5}
                                        p={1}
                                        style={{
                                            width: "100%",
                                            backgroundColor: "#3b3b3b",
                                        }}
                                    >
                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                            <InsightsIcon />
                                            {getTitle(insight)}
                                        </Stack>
                                        <AdvancedInsightsRadioGroupRating
                                            id={insight.id}
                                            existingReaction={insight.reaction}
                                            thumbsUpsCount={insight.thumbs_up}
                                            thumbsDownsCount={insight.thumbs_down}
                                        />
                                    </Stack>
                                    <Stack spacing={2} p={2}>
                                        <p> {insight.title}</p>
                                        <p
                                            style={{
                                                fontSize: "12px",
                                                fontWeight: "200",
                                                textAlign: "left",
                                            }}
                                        >
                                            {limitedText}
                                            {remainingText && (
                                                <Tooltip title={remainingText} arrow>
                                                    <span
                                                        style={{
                                                            color: "#5ac36b",
                                                            cursor: "pointer",
                                                            fontWeight: "300",
                                                        }}
                                                        role="button"
                                                        tabIndex={0}
                                                        onClick={() =>
                                                            setModalOpen(
                                                                `${insight.title}-${index}`
                                                            )
                                                        }
                                                        onKeyDown={(e) => {
                                                            if (
                                                                e.key === "Enter" ||
                                                                e.key === " "
                                                            ) {
                                                                setModalOpen(
                                                                    `${insight.title}-${index}`
                                                                );
                                                            }
                                                        }}
                                                    >
                                                        Read more
                                                    </span>
                                                </Tooltip>
                                            )}
                                        </p>
                                    </Stack>
                                </Stack>
                                <AiInsightsModal
                                    type={getTitle(insight)}
                                    title={insight.title}
                                    body={insight.body}
                                    modalOpen={modalOpen}
                                    setModalOpen={setModalOpen}
                                    index={index}
                                    id={insight.id}
                                    existingReaction={insight.reaction}
                                    thumbsUpsCount={insight.thumbs_up}
                                    thumbsDownsCount={insight.thumbs_down}
                                />
                            </InsightsPaper>
                        );
                    })}
            </SliderContainer>
        </Stack>
    );
};

export default AiInsightsSection;
